"use client";

import { z } from "zod";
import parsePhoneNumberFromString from "libphonenumber-js";
import { IHubSpotForm } from "@/sanity/globals/HubspotForm";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shadcn/ui/form";
import { Input } from "@/shadcn/ui/input";
import { CornerButton } from "@/shadcn/ui/corner-button";
import { stegaClean } from "@sanity/client/stega";
import { submitFormDataToHubSpot } from "@/helpers/hubspot";
import { usePostHog } from "posthog-js/react";
import { sendGTMEvent } from "@next/third-parties/google";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";

export default function HubSpotForm({ formData }: { formData: IHubSpotForm }) {
  const postHog = usePostHog();
  const [loading, setLoading] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const router = useRouter();

  const submitTextRef = React.useRef<HTMLSpanElement>(null);
  const [iconSize, setIconSize] = React.useState<number | null>(null);

  React.useEffect(() => {
    const el = submitTextRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      setIconSize(entry.contentRect.height + parseFloat(getComputedStyle(el).paddingTop) * 2);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const cleanFormData = useMemo(() => {
    return stegaClean(formData);
  }, [formData]);

  const formFields = useMemo(() => {
    return cleanFormData?.fields?.reduce((obj, item) => {
      return {
        ...obj,
        [item.name]:
          item.type === "text"
            ? item.required
              ? z.string().min(1, { message: "Erforderlich" })
              : z.string()
            : item.type === "email"
              ? item.required
                ? z.string().min(1, { message: "Erforderlich" }).email({ message: "Ungültige E-Mail" })
                : z.string().email({ message: "Ungültige E-Mail" }).optional()
              : item.type === "phone"
                ? z.string().transform((arg, ctx) => {
                    const phone = parsePhoneNumberFromString(arg, {
                      defaultCountry: "DE",
                      extract: false,
                    });

                    if (phone && phone.isValid()) {
                      return phone.number;
                    }

                    if ((arg === "" || arg === null || arg === undefined) && !item.required) {
                      return arg;
                    }

                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: item.required ? "Erforderlich" : "Ungültige Telefonnummer",
                    });

                    return z.NEVER;
                  })
                : item.type === "number"
                  ? item.required
                    ? z.coerce
                        .number({
                          invalid_type_error: "Ungültige Zahl",
                        })
                        .min(0, { message: "Zahl muss positiv sein" })
                    : z.coerce
                        .number({
                          invalid_type_error: "Ungültige Zahl",
                        })
                        .min(0, { message: "Zahl muss positiv sein" })
                        .optional()
                  : z.unknown(),
      };
    }, {});
  }, [cleanFormData]);

  const formSchema = useMemo(() => {
    return z.object(formFields);
  }, [formFields]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: cleanFormData?.fields?.reduce((obj, item) => {
      return {
        ...obj,
        [item.name]: "",
      };
    }),
  });

  async function onSubmit(fieldData: any) {
    if (cleanFormData && fieldData) {
      setLoading(true);
      if (fieldData.email) {
        postHog.identify(fieldData.email);
      }
      postHog.capture("form_submit", {
        form: cleanFormData._id,
        formTitle: cleanFormData.name,
        fields: fieldData,
      });
      const res = await submitFormDataToHubSpot(fieldData, formData, window?.location?.href, document?.title);
      if (res?.success === true) {
        sendGTMEvent({
          event: "hsFormSubmit",
          "hs-form-guid": cleanFormData?.settings?.formId ?? null,
          pageName: document.title,
          pagePath: window.location.href,
          // @ts-ignore
          "form-firstname": formData?.firstName,
          // @ts-ignore
          "form-email": formData?.email,
        });

        if (cleanFormData?.settings?.onSubmit === "message") {
          setSuccessMessage(cleanFormData?.settings?.message ?? "Form submitted successfully");
        } else if (cleanFormData?.settings?.onSubmit === "redirect") {
          const redirectUrl = resolvePageLink(cleanFormData?.settings?.redirect ?? null);
          if (redirectUrl) {
            router.push(redirectUrl);
          } else {
            setSuccessMessage(`Form submitted successfully. Could not redirect to page ${redirectUrl}`);
          }
        }
      } else {
        setLoading(false);
        setErrorMessage("Form submission failed. Try again later.");
      }
    }
  }

  return (
    <>
      {cleanFormData?.fields ? (
        <Form {...form}>
          <form
            className="flex relative flex-col justify-start items-stretch w-[min(49rem,100%)] gap-[1.5rem]"
            onSubmit={form.handleSubmit(onSubmit)}>
            {cleanFormData?.fields?.map((formField, index) => {
              return (
                <FormField
                  key={index}
                  control={form.control}
                  // @ts-ignore - this is a valid prop
                  name={formField.name}
                  disabled={loading}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{formField.label}</FormLabel>
                      <FormControl>
                        {
                          // @ts-ignore - this is a valid prop
                          <Input
                            className="bg-[#eee]"
                            type={formField.type === "number" ? "number" : formField.type}
                            min={formField.type === "number" ? 0 : undefined}
                            placeholder={formField.placeholder}
                            {...field}
                            value={undefined}
                          />
                        }
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              );
            })}
            <div className="relative z-10 w-full">
              <button
                type="submit"
                disabled={loading}
                style={iconSize ? ({ "--cta-icon-size": `${iconSize}px` } as React.CSSProperties) : undefined}
                className="inline-flex relative z-10 items-center w-full text-black bg-transparent disabled:opacity-50 disabled:pointer-events-none cta cta-wrapper has-icon group text-[2.25rem] font-geist leading-[normal]">
                <span
                  aria-hidden
                  className="absolute inset-y-0 left-0 pointer-events-none cta-bg z-[1] rounded-[1.25rem] bg-[#98D8FF]"
                />

                <span
                  ref={submitTextRef}
                  className="flex relative items-center w-full h-full cta-button z-[2] px-[4rem] py-[2rem] sm:px-[2.25rem]">
                  <span className="relative mx-auto button-text overflow-clip">
                    <span className="block transition-all duration-300 pr-[.5rem] button-text-initial">
                      {formData?.settings?.submitText ?? "Submit"}
                    </span>
                    <span className="absolute transition-all duration-300 pointer-events-none button-text-hover top-[3rem] pr-[.5rem]">
                      {formData?.settings?.submitText ?? "Submit"}
                    </span>
                  </span>
                </span>

                <span
                  className="button-icon-box button-shape-default relative z-[2] ml-[.5rem] shrink-0 hidden sm:flex items-center justify-center overflow-clip bg-[#98D8FF] text-black [&_svg]:w-[3rem] [&_svg]:h-[3rem] [&_svg]:shrink-0"
                  style={iconSize ? { width: iconSize, height: iconSize } : undefined}>
                  <span className="flex justify-center items-center w-full h-full button-icon rotate-[-45deg]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
                      viewBox="0 0 24 24"
                      fill="none">
                      <rect
                        width="24"
                        height="24"
                        fill="none"
                        fillOpacity="0.01"
                      />
                      <path
                        d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                </span>
              </button>
            </div>
            {successMessage && (
              <div className="w-full font-bold text-green-900 bg-green-100 rounded-sm border border-green-500 py-[1rem] px-[2rem] drop-shadow-md">
                {successMessage}
              </div>
            )}
            {errorMessage && (
              <div className="w-full font-bold text-red-900 bg-red-100 rounded-sm border border-red-500 py-[1rem] px-[2rem] drop-shadow-md">
                {errorMessage}
              </div>
            )}
          </form>
        </Form>
      ) : (
        <div className="p-20 font-bold text-blue-900 bg-gray-100 rounded-xl border-2 border-black">
          MISSING FORM DATA
        </div>
      )}
    </>
  );
}
